import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  CollectorAvailabilityAssignment,
  CollectorAvailabilityTimelineSegment
} from '../../domain/collector-availability-overview.model';
import { DateTime } from 'luxon';
import {
  AVAILABILITY_DAY_MINUTES,
  formatAssignmentDisplayStatus,
  formatMinuteOfDay,
  minuteToPercentage
} from './availability-overview.utils';

@Component({
  selector: 'app-availability-timeline',
  templateUrl: './availability-timeline.component.html',
  styleUrls: ['./availability-timeline.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvailabilityTimelineComponent implements OnChanges {
  @Input() collectorName = 'Collector';
  @Input() segments: CollectorAvailabilityTimelineSegment[] = [];
  @Input() selectedFromMinute = 0;
  @Input() selectedToMinute = AVAILABILITY_DAY_MINUTES;
  @Input() clearPinnedTick = 0;
  @Output() pinnedChange = new EventEmitter<boolean>();

  displaySegments: CollectorAvailabilityTimelineSegment[] = [];
  activeSegment: CollectorAvailabilityTimelineSegment | null = null;
  pinnedSegment: CollectorAvailabilityTimelineSegment | null = null;
  readonly hourLabels = [0, 360, 720, 1080, 1440];
  readonly hourGrid = Array.from({ length: 25 }, (_, index) => index * 60);
  readonly minuteToPercentage = minuteToPercentage;
  readonly formatMinuteOfDay = formatMinuteOfDay;
  readonly formatAssignmentDisplayStatus = formatAssignmentDisplayStatus;

  private lastClearPinnedTick = 0;

  ngOnChanges(changes: SimpleChanges): void {
    const shouldClearPinned = this.clearPinnedTick !== this.lastClearPinnedTick;

    if (changes.segments) {
      const pinnedSegment = this.pinnedSegment;
      const activeSegment = this.activeSegment;
      this.displaySegments = this.mergeAdjacentBusySegments(this.segments);

      if (!shouldClearPinned) {
        this.pinnedSegment = this.findMatchingInteractiveSegment(pinnedSegment);
        this.activeSegment =
          this.pinnedSegment ?? this.findMatchingInteractiveSegment(activeSegment);

        if (pinnedSegment && !this.pinnedSegment) {
          this.pinnedChange.emit(false);
        }
      }
    }

    if (shouldClearPinned) {
      this.lastClearPinnedTick = this.clearPinnedTick;
      this.pinnedSegment = null;
      this.activeSegment = null;
      this.pinnedChange.emit(false);
    }
  }

  segmentLeft(segment: CollectorAvailabilityTimelineSegment): number {
    return minuteToPercentage(segment.fromMinute);
  }

  segmentWidth(segment: CollectorAvailabilityTimelineSegment): number {
    return Math.max(
      0,
      minuteToPercentage(segment.toMinute) - minuteToPercentage(segment.fromMinute)
    );
  }

  isSelected(segment: CollectorAvailabilityTimelineSegment): boolean {
    return (
      segment.toMinute > this.selectedFromMinute &&
      segment.fromMinute < this.selectedToMinute
    );
  }

  assignmentsLabel(segment: CollectorAvailabilityTimelineSegment): string {
    const count = segment.assignments?.length ?? 0;
    return count > 1 ? `${count} assigned events` : '1 assigned event';
  }

  assignmentLabel(assignment: CollectorAvailabilityAssignment): string {
    const status = formatAssignmentDisplayStatus(assignment.displayStatus);
    return `${assignment.game.label} · ${assignment.eventCode} · ${this.formatAssignmentTime(
      assignment.startsAt
    )}–${this.formatAssignmentTime(assignment.endsAt)}${
      status ? ` · ${status}` : ''
    }`;
  }

  formatAssignmentTime(timestamp: string): string {
    const value = DateTime.fromISO(timestamp, { setZone: true }).setZone(
      'Europe/Zurich'
    );
    return value.isValid ? value.toFormat('HH:mm') : '';
  }

  onSegmentEnter(segment: CollectorAvailabilityTimelineSegment): void {
    if (segment.state !== 'busy' || segment.assignments.length === 0) {
      return;
    }
    if (!this.pinnedSegment) {
      this.activeSegment = segment;
    }
  }

  onSegmentLeave(): void {
    if (!this.pinnedSegment) {
      this.activeSegment = null;
    }
  }

  onSegmentFocus(segment: CollectorAvailabilityTimelineSegment): void {
    this.onSegmentEnter(segment);
  }

  onSegmentClick(segment: CollectorAvailabilityTimelineSegment): void {
    if (segment.state !== 'busy' || segment.assignments.length === 0) {
      return;
    }
    if (this.pinnedSegment === segment) {
      return;
    }
    this.pinnedSegment = segment;
    this.activeSegment = segment;
    this.pinnedChange.emit(true);
  }

  closePinned(event: Event): void {
    event.stopPropagation();
    this.pinnedSegment = null;
    this.activeSegment = null;
    this.pinnedChange.emit(false);
  }

  currentDetails(): CollectorAvailabilityTimelineSegment | null {
    return this.pinnedSegment ?? this.activeSegment;
  }

  trackBySegment(index: number, segment: CollectorAvailabilityTimelineSegment): string {
    return `${index}-${segment.fromMinute}-${segment.toMinute}-${segment.state}`;
  }

  private findMatchingInteractiveSegment(
    target: CollectorAvailabilityTimelineSegment | null
  ): CollectorAvailabilityTimelineSegment | null {
    if (!target) {
      return null;
    }

    return (
      this.displaySegments.find(
        (segment) =>
          segment.fromMinute === target.fromMinute &&
          segment.toMinute === target.toMinute &&
          segment.state === target.state &&
          segment.state === 'busy' &&
          segment.assignments.length > 0
      ) ?? null
    );
  }

  private mergeAdjacentBusySegments(
    segments: CollectorAvailabilityTimelineSegment[]
  ): CollectorAvailabilityTimelineSegment[] {
    const merged: CollectorAvailabilityTimelineSegment[] = [];

    for (const segment of segments ?? []) {
      const previous = merged[merged.length - 1];
      if (
        previous?.state === 'busy' &&
        segment.state === 'busy' &&
        previous.toMinute === segment.fromMinute
      ) {
        previous.toMinute = segment.toMinute;
        previous.assignments = this.mergeAssignments(
          previous.assignments,
          segment.assignments
        );
        continue;
      }

      merged.push({
        ...segment,
        assignments: [...(segment.assignments ?? [])]
      });
    }

    return merged;
  }

  private mergeAssignments(
    left: CollectorAvailabilityAssignment[],
    right: CollectorAvailabilityAssignment[]
  ): CollectorAvailabilityAssignment[] {
    const byEventId = new Map(
      [...(left ?? []), ...(right ?? [])].map((assignment) => [
        assignment.eventId,
        assignment
      ])
    );
    return [...byEventId.values()];
  }
}
